import type { Sort } from '@schibsted-nmp/search-frontend-clients/quest';
import { useQuery, type QueryFunctionContext } from '@tanstack/react-query';
import { useAppConfig } from '../AppConfig';
import { getSortQueryKey } from './SortQuery';

interface SortQueryMeta {
    apiUrl: string;
}

declare module '@tanstack/react-query' {
    // @ts-expect-error Duplicate identifier 'QueryMeta'.ts(2300)
    type QueryMeta = SortQueryMeta;
}

export function useSortData() {
    const { apiUrl, searchKey } = useAppConfig();

    const sortResult = useQuery({
        meta: {
            apiUrl,
        },
        queryFn: fetchSortData,
        queryKey: getSortQueryKey({ searchKey }),
        ...getQueryCacheOptions(),
    });

    return {
        sortData: sortResult.data ?? [],
    };
}

function getQueryCacheOptions() {
    // These options combined will make sure that we have referential equality
    // for the current data between renders, as long as the query doesn't
    // change. We need referential equality to ensure that hooks or components
    // using the data as a dependency don't update unless the query has changed.

    const keepPreviousData = <T>(previousData: T) => previousData;

    return {
        // Keep the current data while fetching data for a new query.
        placeholderData: keepPreviousData,
        // Never mark cached data as stale, to avoid refetching data for the
        // current query.
        staleTime: Infinity,
    };
}

type SortQueryKey = ReturnType<typeof getSortQueryKey>;

async function fetchSortData({
    meta,
    signal,
    queryKey,
}: QueryFunctionContext<SortQueryKey>): Promise<Sort[]> {
    const { apiUrl } = meta ?? {};
    const [{ searchKey }] = queryKey;

    const url = new URL(`${apiUrl}/sort/${searchKey}`);

    try {
        const response = await fetch(url, {
            headers: {
                Accept: 'application/json',
            },
            signal,
        });
        if (!response.ok) {
            const message = await response.text();
            return Promise.reject(
                new Error(
                    `Got status ${response.status} calling ${url}: ${message}`,
                ),
            );
        }
        return response.json();
    } catch (err) {
        return Promise.reject(
            new Error(`Error calling ${url}`, { cause: err }),
        );
    }
}
